import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

interface InviteData {
    valid: boolean;
    tenantName: string;
    email: string;
    role: string;
    userExists: boolean;
    reason?: string;
}

const registerSchema = z.object({
    name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
    password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
    confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
    message: "Senhas não conferem",
    path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export function InvitePage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    // const { login } = useAuth(); // login not used, frontend redirects

    const [loading, setLoading] = useState(true);
    const [inviteData, setInviteData] = useState<InviteData | null>(null);
    const [error, setError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm<RegisterFormData>({
        resolver: zodResolver(registerSchema)
    });

    const verifyToken = useCallback(async () => {
        try {
            const response = await fetch(`/api/invites/verify?token=${token}`);
            const data = await response.json();

            if (!data.valid) {
                let msg = 'Convite inválido.';
                if (data.reason === 'expired') msg = 'Este convite expirou.';
                if (data.reason === 'already_accepted') msg = 'Este convite já foi aceito.';
                if (data.reason === 'not_found') msg = 'Convite não encontrado.';
                setError(msg);
            } else {
                setInviteData(data);
            }
        } catch (err) {
            setError('Erro ao verificar convite.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setError('Token de convite inválido ou ausente.');
            setLoading(false);
            return;
        }
        verifyToken();
    }, [token, verifyToken]);

    const handleAccept = async (formData?: RegisterFormData) => {
        try {
            const payload = {
                token,
                ...(formData && {
                    name: formData.name,
                    password: formData.password
                })
            };

            // If user exists, we might need auth token if we want to secure it, 
            // but the API handles the link logic based on the secure token hash.
            // However, if the user IS logged in, we should send the auth header 
            // so backend can verify the logged user matches the invite email.

            const authToken = localStorage.getItem('auth_token');
            const headers: HeadersInit = {
                'Content-Type': 'application/json'
            };
            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }

            const response = await fetch('/api/invites/accept', {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Falha ao aceitar convite');
            }

            await response.json();

            toast.success('Convite aceito com sucesso!');

            if (inviteData) {
                if (!authToken) {
                    navigate('/login', {
                        state: {
                            email: inviteData.email,
                            message: 'Conta criada com sucesso! Faça login para continuar.'
                        }
                    });
                } else {
                    // Mostrar mensagem de sucesso e aguardar antes de redirecionar
                    toast.success(`Você agora faz parte de "${inviteData.tenantName}"!`, {
                        duration: 2000
                    });

                    // Aguardar toast ser visto e redirecionar
                    setTimeout(() => {
                        window.location.href = '/dashboard';
                    }, 1500);
                }
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao aceitar convite';
            toast.error(errorMessage);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white p-8 rounded-3xl shadow-xl max-w-md w-full text-center border border-red-100">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
                        ⚠️
                    </div>
                    <h2 className="text-2xl font-bold font-oswald text-gray-800 mb-2">Ops! Algo deu errado</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold font-oswald hover:bg-gray-800 transition-all"
                    >
                        Voltar para Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 relative overflow-hidden">
            {/* Background decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-blue-200/20 rounded-full blur-3xl rounded-full" />
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] bg-purple-200/20 rounded-full blur-3xl rounded-full" />
            </div>

            <div className="bg-white/80 backdrop-blur-xl p-8 md:p-12 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.05)] max-w-lg w-full border border-white/60 relative z-10">
                <div className="text-center mb-8">
                    <span className="inline-block p-3 bg-blue-50 rounded-2xl text-4xl mb-4 shadow-sm">
                        ✉️
                    </span>
                    <h1 className="text-3xl font-bold font-oswald text-gray-900 mb-2">
                        Convite para {inviteData.tenantName}
                    </h1>
                    <p className="text-gray-500">
                        Você foi convidado para participar como <strong className="text-blue-600 uppercase">{inviteData.role}</strong>
                    </p>
                    <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-100 inline-block px-6">
                        <span className="text-gray-600 font-medium">{inviteData.email}</span>
                    </div>
                </div>

                {inviteData.userExists ? (
                    <div className="space-y-6">
                        <div className="bg-blue-50/50 border border-blue-100 p-4 rounded-xl text-blue-800 text-sm text-center">
                            Identificamos que você já possui uma conta com este email.
                        </div>

                        {!localStorage.getItem('auth_token') ? (
                            <>
                                <button
                                    onClick={() => navigate('/login', {
                                        state: {
                                            email: inviteData.email,
                                            returnUrl: window.location.pathname + window.location.search,
                                            message: 'Faça login para aceitar o convite.'
                                        }
                                    })}
                                    className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold font-oswald text-lg hover:bg-gray-800 transition-all transform hover:-translate-y-0.5"
                                >
                                    Fazer Login para Aceitar
                                </button>
                                <p className="text-xs text-center text-gray-400">
                                    É necessário estar logado com a conta <b>{inviteData.email}</b>
                                </p>
                            </>
                        ) : (
                            <button
                                onClick={() => handleAccept()}
                                className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold font-oswald text-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all transform hover:-translate-y-0.5"
                            >
                                Aceitar e Entrar
                            </button>
                        )}

                        {localStorage.getItem('auth_token') && (
                            <p className="text-xs text-center text-gray-400">
                                Ao aceitar, você será vinculado à organização automaticamente.
                            </p>
                        )}
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(handleAccept)} className="space-y-5">
                        <div className="text-left">
                            <label className="block text-sm font-bold font-oswald text-gray-700 mb-1.5 uppercase tracking-wide">
                                Seu Nome Completo
                            </label>
                            <input
                                {...register('name')}
                                type="text"
                                placeholder="João Silva"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                            {errors.name && <span className="text-red-500 text-xs ml-1">{errors.name.message}</span>}
                        </div>

                        <div className="text-left">
                            <label className="block text-sm font-bold font-oswald text-gray-700 mb-1.5 uppercase tracking-wide">
                                Defina uma Senha
                            </label>
                            <input
                                {...register('password')}
                                type="password"
                                placeholder="••••••••"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono"
                            />
                            {errors.password && <span className="text-red-500 text-xs ml-1">{errors.password.message}</span>}
                        </div>

                        <div className="text-left">
                            <label className="block text-sm font-bold font-oswald text-gray-700 mb-1.5 uppercase tracking-wide">
                                Confirme a Senha
                            </label>
                            <input
                                {...register('confirmPassword')}
                                type="password"
                                placeholder="••••••••"
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-mono"
                            />
                            {errors.confirmPassword && <span className="text-red-500 text-xs ml-1">{errors.confirmPassword.message}</span>}
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold font-oswald text-lg hover:shadow-lg hover:shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 mt-4"
                        >
                            {isSubmitting ? 'Criando Conta...' : 'Criar Conta e Aceitar'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
